import { createPortal } from "react-dom";
import { Clock, FileText, Loader2, X } from "lucide-react";
import { useAuditRecord } from "@/modules/audit/hooks/useAudit";
import type { AuditEvent } from "@/modules/audit/types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityId: string;
  entityLabel?: string;
};

function formatFull(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  if (diffDay < 30)
    return `about ${Math.floor(diffDay / 7)} week${
      Math.floor(diffDay / 7) === 1 ? "" : "s"
    } ago`;
  if (diffDay < 365)
    return `about ${Math.floor(diffDay / 30)} month${
      Math.floor(diffDay / 30) === 1 ? "" : "s"
    } ago`;
  return `about ${Math.floor(diffDay / 365)} year${
    Math.floor(diffDay / 365) === 1 ? "" : "s"
  } ago`;
}

function actionLabel(action: string): string {
  return action.toLowerCase().replace(/_/g, " ");
}

function formatDiffValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.length === 0 ? "—" : v.join(", ");
  try {
    return JSON.stringify(v);
  } catch {
    return "[object]";
  }
}

function formatDiffFieldName(field: string): string {
  const titleCase = (segment: string): string => {
    const spaced = segment
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .trim()
      .toLowerCase();
    if (!spaced) return segment;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };
  return field.split(".").map(titleCase).join(" → ");
}

const MAX_INLINE_CHANGES = 3;

function TimelineItem({ event }: { event: AuditEvent }) {
  return (
    <div className="flex gap-3 border-b border-border px-5 py-4 last:border-b-0">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]">
        <FileText className="h-3 w-3" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground">
          {actionLabel(event.action)}
        </div>
        {event.description && (
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            {event.description}
          </div>
        )}
        {event.fieldChanges && event.fieldChanges.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {event.fieldChanges.slice(0, MAX_INLINE_CHANGES).map((fc) => (
              <div
                key={fc.field}
                className="flex flex-wrap items-center gap-1 text-[11px] leading-tight"
              >
                <span className="text-muted-foreground">
                  {formatDiffFieldName(fc.field)}:
                </span>
                <span
                  className="line-through"
                  style={{ color: "hsl(0,62%,48%)" }}
                >
                  {formatDiffValue(fc.before)}
                </span>
                <span className="text-muted-foreground">→</span>
                <span
                  className="font-medium"
                  style={{ color: "hsl(142,72%,28%)" }}
                >
                  {formatDiffValue(fc.after)}
                </span>
              </div>
            ))}
            {event.fieldChanges.length > MAX_INLINE_CHANGES && (
              <div className="text-[11px] text-muted-foreground">
                + {event.fieldChanges.length - MAX_INLINE_CHANGES} more change
                {event.fieldChanges.length - MAX_INLINE_CHANGES === 1
                  ? ""
                  : "s"}
              </div>
            )}
          </div>
        )}
        <div className="mt-1.5 text-[12px] text-muted-foreground">
          {(event.performedByName ?? "System") + " · "}
          {formatFull(event.createdAt)} · {relativeTime(event.createdAt)}
        </div>
      </div>
    </div>
  );
}

export function ActivityHistoryDrawer({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityLabel,
}: Props) {
  const { data, isLoading, isError } = useAuditRecord(
    entityType,
    entityId,
    isOpen,
  );

  const events = data?.data ?? [];

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          zIndex: 9998,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "420px",
          zIndex: 9999,
          backgroundColor: "white",
          borderLeft: "1px solid hsl(210,30%,85%)",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            height: "56px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            gap: "10px",
            borderBottom: "1px solid hsl(210,30%,85%)",
            backgroundColor: "white",
          }}
        >
          <div className="flex items-center gap-2.5">
            <Clock
              className="text-muted-foreground"
              style={{ width: 16, height: 16 }}
              strokeWidth={1.5}
            />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                Activity History
              </div>
              {entityLabel && (
                <div
                  className="text-muted-foreground"
                  style={{ fontSize: 12 }}
                >
                  {entityLabel}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            style={{ width: 24, height: 24 }}
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                strokeWidth={1.5}
              />
              Loading…
            </div>
          ) : isError ? (
            <div className="p-6 text-sm text-destructive">
              Failed to load activity.
            </div>
          ) : events.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No activity recorded yet.
            </div>
          ) : (
            <div>
              {events.map((e) => (
                <TimelineItem key={e.id} event={e} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
